/**
 * Mobile Navigation Component
 * Version: 6.2.0
 */
import { useState, useEffect, useRef } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useSwipe } from '../hooks'
import './MobileNav.css'

interface NavItem {
  id: string
  label: string
  icon: string
  path: string
  badge?: number
}

const NAV_ITEMS: NavItem[] = [
  { id: 'dashboard', label: 'Dashboard', icon: '📊', path: '/' },
  { id: 'brokers', label: 'Brokers', icon: '🔗', path: '/brokers' },
  { id: 'strategies', label: 'Strategies', icon: '📈', path: '/strategies' },
  { id: 'settings', label: 'Settings', icon: '⚙️', path: '/settings' },
]

interface MobileNavProps {
  onOpenSettings?: () => void
}

function MobileNav({ onOpenSettings }: MobileNavProps) {
  const location = useLocation()
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState('dashboard')
  const navRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const currentPath = location.pathname
    const activeItem = NAV_ITEMS.find(item => item.path === currentPath)
    if (activeItem) {
      setActiveTab(activeItem.id)
    }
  }, [location.pathname])

  // Swipe navigation - hooks into event handlers
  useSwipe(navRef, {
    threshold: 50,
    onSwipeLeft: () => {
      // Swipe left = go to next tab
      const currentIndex = NAV_ITEMS.findIndex(item => item.id === activeTab)
      if (currentIndex < NAV_ITEMS.length - 1) {
        const nextItem = NAV_ITEMS[currentIndex + 1]
        handleNavClick(nextItem)
      }
    },
    onSwipeRight: () => {
      // Swipe right = go to previous tab
      const currentIndex = NAV_ITEMS.findIndex(item => item.id === activeTab)
      if (currentIndex > 0) {
        const prevItem = NAV_ITEMS[currentIndex - 1]
        handleNavClick(prevItem)
      }
    }
  })

  const handleNavClick = (item: NavItem) => {
    setActiveTab(item.id)
    navigate(item.path)
    
    if (item.id === 'settings' && onOpenSettings) {
      onOpenSettings()
    }
  }

  return (
    <nav ref={navRef} className="mobile-nav">
      <div className="mobile-nav-items">
        {NAV_ITEMS.map((item) => (
          <button
            key={item.id}
            className={`mobile-nav-item ${activeTab === item.id ? 'active' : ''}`}
            onClick={() => handleNavClick(item)}
            aria-label={item.label}
          >
            <span className="nav-icon">
              {item.icon}
              {item.badge && item.badge > 0 && (
                <span className="nav-badge">{item.badge > 99 ? '99+' : item.badge}</span>
              )}
            </span>
            <span className="nav-label">{item.label}</span>
          </button>
        ))}
      </div>
      {/* Swipe hint */}
      <div className="swipe-hint-nav">← Swipe to navigate →</div>
    </nav>
  )
}

// Mobile Header Component
interface MobileHeaderProps {
  title: string
  showBack?: boolean
  onBack?: () => void
  onMenuToggle?: () => void
  onSearch?: () => void
  actions?: React.ReactNode
}

function MobileHeader({ 
  title, 
  showBack = false, 
  onBack, 
  onMenuToggle,
  onSearch,
  actions 
}: MobileHeaderProps) {
  return (
    <header className="mobile-header">
      <div className="mobile-header-content">
        <div className="mobile-header-left">
          {showBack ? (
            <button className="mobile-header-btn" onClick={onBack} aria-label="Go back">
              ←
            </button>
          ) : (
            <button className="mobile-header-btn" onClick={onMenuToggle} aria-label="Menu">
              ☰
            </button>
          )}
        </div>
        
        <h1 className="mobile-header-title">{title}</h1>
        
        <div className="mobile-header-actions">
          {onSearch && (
            <button className="mobile-header-btn" onClick={onSearch} aria-label="Search">
              🔍
            </button>
          )}
          {actions}
        </div>
      </div>
    </header>
  )
}

// Mobile Search Component
interface MobileSearchProps {
  value: string
  onChange: (value: string) => void
  onClose?: () => void
  placeholder?: string
}

function MobileSearch({ 
  value, 
  onChange, 
  onClose,
  placeholder = 'Search...' 
}: MobileSearchProps) {
  return (
    <div className="mobile-search">
      <div className="mobile-search-bar">
        <span>🔍</span>
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          autoFocus
        />
        {value && (
          <button 
            className="search-clear" 
            onClick={() => onChange('')}
            aria-label="Clear search"
          >
            ✕
          </button>
        )}
        {onClose && (
          <button className="search-close" onClick={onClose}>
            Cancel
          </button>
        )}
      </div>
    </div>
  )
}

// Mobile Quick Actions
interface QuickAction {
  id: string
  label: string
  icon: string
  onClick: () => void
  color?: string
}

interface MobileQuickActionsProps {
  actions: QuickAction[]
}

function MobileQuickActions({ actions }: MobileQuickActionsProps) {
  return (
    <div className="mobile-quick-actions">
      <div className="quick-action-grid">
        {actions.map((action) => (
          <button
            key={action.id}
            className="quick-action-item ripple"
            onClick={action.onClick}
            style={action.color ? { borderColor: action.color } : undefined}
          >
            <span className="quick-action-icon">{action.icon}</span>
            <span className="quick-action-label">{action.label}</span>
          </button>
        ))}
      </div>
    </div>
  )
}

// Mobile Bottom Sheet Modal
interface MobileBottomSheetProps {
  isOpen: boolean
  onClose: () => void
  title: string
  children: React.ReactNode
}

function MobileBottomSheet({ isOpen, onClose, title, children }: MobileBottomSheetProps) {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
    return () => {
      document.body.style.overflow = ''
    }
  }, [isOpen])

  if (!isOpen) return null

  return (
    <>
      <div 
        className={`mobile-modal-overlay ${isOpen ? 'show' : ''}`} 
        onClick={onClose}
        aria-hidden="true"
      />
      <div className={`mobile-bottom-sheet ${isOpen ? 'show' : ''}`} role="dialog" aria-modal="true">
        <div className="bottom-sheet-handle" />
        <div className="bottom-sheet-header">
          <h3 className="bottom-sheet-title">{title}</h3>
          <button className="bottom-sheet-close" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </div>
        <div className="bottom-sheet-content">
          {children}
        </div>
      </div>
    </>
  )
}

export { 
  MobileNav, 
  MobileHeader, 
  MobileSearch, 
  MobileQuickActions, 
  MobileBottomSheet 
}